import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowRightLeft,
  ChefHat,
  ChevronUp,
  Clock,
  Pencil,
  CreditCard,
  Percent,
  Send,
  Users,
  Wine,
} from 'lucide-react';
import clsx from 'clsx';
import {
  COURSES,
  formatRate,
  type MenuItem,
  type Order,
  type OrderItem,
} from '@sebastian/shared';
import { api, ApiError } from '../lib/api';
import { useCategoryTree, useMenuItems, useOrder, useSettings, useTables } from '../hooks/queries';
import { useNow } from '../hooks/useNow';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../lib/auth';
import { useCurrency } from '../lib/currency';
import { formatClock, formatWaiting } from '../lib/format';
import { DiscountDialog, ItemDiscountDialog } from '../components/DiscountDialog';
import { CloseOrderDialog, InvoiceBuyerSummary } from '../components/InvoiceBuyerDialog';
import { MenuBrowser } from '../components/MenuBrowser';
import { ModifierDialog, type ItemDraft } from '../components/ModifierDialog';
import { MoveItemDialog } from '../components/MoveItemDialog';
import { OrderPanel, type OrderPanelHandlers } from '../components/OrderPanel';
import { SeatingDialog } from '../components/SeatingDialog';
import { PaymentDialog } from '../components/PaymentDialog';
import { ReceiptSheet, PrintReceiptButton, SendToPrinterButton } from '../components/ReceiptSheet';
import {
  Button,
  BusyOverlay,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Modal,
  Spinner,
  Textarea,
} from '../components/ui';

const RECENT_KEY = 'sebastian.recentItems';

function readRecent(): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]') as string[];
  } catch {
    return [];
  }
}

/**
 * The order screen. Three panes on a tablet in landscape: categories, items,
 * and a sticky order panel — the layout the specification asks for, arranged so
 * the common path (pick item → send → pay) is never more than two taps.
 */
export function OrderPage() {
  const { tableId } = useParams<{ tableId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { money } = useCurrency();
  const { allows } = useAuth();
  const now = useNow(30_000);

  const [orderId, setOrderId] = useState<string | null>(null);
  const [loadingOrder, setLoadingOrder] = useState(true);
  const [loadError, setLoadError] = useState<unknown>(null);

  // Resolve the table's active order once, then poll it by id.
  useEffect(() => {
    if (!tableId) return;
    let cancelled = false;
    setLoadingOrder(true);
    api.orders
      .activeForTable(tableId)
      .then((order) => {
        if (cancelled) return;
        if (!order) {
          toast.info('That table has no open order.');
          navigate('/pos', { replace: true });
          return;
        }
        setOrderId(order.id);
        queryClient.setQueryData(['order', order.id], order);
      })
      .catch((caught: unknown) => !cancelled && setLoadError(caught))
      .finally(() => !cancelled && setLoadingOrder(false));
    return () => {
      cancelled = true;
    };
  }, [tableId, navigate, queryClient, toast]);

  const { data: order, error: orderError } = useOrder(orderId ?? undefined);
  const { data: categories = [] } = useCategoryTree();
  const { data: menuItems = [] } = useMenuItems(false, true);

  const [course, setCourse] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [configuring, setConfiguring] = useState<MenuItem | null>(null);
  const [reseating, setReseating] = useState(false);
  const [noteTarget, setNoteTarget] = useState<OrderItem | null>(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [cancelTarget, setCancelTarget] = useState<OrderItem | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  /** The line whose Move button was tapped; null when moving from the footer. */
  const [moveTarget, setMoveTarget] = useState<OrderItem | null>(null);
  const [moving, setMoving] = useState(false);
  const [discountTarget, setDiscountTarget] = useState<OrderItem | null>(null);
  const [paying, setPaying] = useState(false);
  const [discounting, setDiscounting] = useState(false);
  /* Mobile only: the desktop layout ignores this and always shows the column. */
  const [sheetOpen, setSheetOpen] = useState(false);
  const [resolvingUnsent, setResolvingUnsent] = useState(false);
  const [receiptOrder, setReceiptOrder] = useState<Order | null>(null);
  const [choosingBuyer, setChoosingBuyer] = useState(false);
  const { data: settings } = useSettings();
  const [recent, setRecent] = useState<string[]>(readRecent);

  const draftItems = useMemo(
    () => order?.items.filter((item) => item.status === 'DRAFT') ?? [],
    [order],
  );

  // Drop selections for items that have since been sent or removed.
  useEffect(() => {
    setSelectedIds((current) => {
      const live = new Set(draftItems.map((item) => item.id));
      const next = new Set([...current].filter((id) => live.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [draftItems]);

  const invalidate = () => {
    if (orderId) void queryClient.invalidateQueries({ queryKey: ['order', orderId] });
    void queryClient.invalidateQueries({ queryKey: ['tables'] });
    void queryClient.invalidateQueries({ queryKey: ['tickets'] });
  };

  const failed = (caught: unknown, fallback: string) => {
    toast.error(caught instanceof ApiError ? caught.message : fallback);
    if (caught instanceof ApiError && caught.code === 'OUT_OF_STOCK') {
      void queryClient.invalidateQueries({ queryKey: ['menu-items'] });
    }
  };

  const { data: tables } = useTables();

  // Table and guest count both ride on the same PATCH, so a re-seat that
  // changes each of them is one request and either lands or does not.
  const reseat = useMutation({
    mutationFn: (change: { tableId: string; guestCount: number }) =>
      api.orders.update(orderId!, change),
    onSuccess: (updated) => {
      queryClient.setQueryData(['order', updated.id], updated);
      void queryClient.invalidateQueries({ queryKey: ['tables'] });
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
      setReseating(false);
      toast.success(`Table ${updated.tableNumber} · ${updated.guestCount} guests.`);
    },
    onError: (caught) => failed(caught, 'Could not change the table.'),
  });

  const addItem = useMutation({
    mutationFn: ({ item, draft }: { item: MenuItem; draft: ItemDraft }) =>
      api.orders.addItems(orderId!, [
        {
          menuItemId: item.id,
          quantity: draft.quantity,
          course: draft.course,
          notes: draft.notes,
          modifiers: draft.modifiers,
        },
      ]),
    onSuccess: (updated, variables) => {
      queryClient.setQueryData(['order', updated.id], updated);
      void queryClient.invalidateQueries({ queryKey: ['tables'] });
      if (variables.item.trackStock) void queryClient.invalidateQueries({ queryKey: ['menu-items'] });
      setRecent((current) => {
        const next = [variables.item.id, ...current.filter((id) => id !== variables.item.id)].slice(0, 12);
        localStorage.setItem(RECENT_KEY, JSON.stringify(next));
        return next;
      });
    },
    onError: (caught) => failed(caught, 'Could not add that item.'),
  });

  const updateItem = useMutation({
    mutationFn: ({ itemId, body }: { itemId: string; body: Record<string, unknown> }) =>
      api.orders.updateItem(orderId!, itemId, body),
    onSuccess: (updated, variables) => {
      queryClient.setQueryData(['order', updated.id], updated);
      if ('quantity' in variables.body) void queryClient.invalidateQueries({ queryKey: ['menu-items'] });
    },
    onError: (caught) => failed(caught, 'Could not update that item.'),
  });

  const removeItem = useMutation({
    mutationFn: (itemId: string) => api.orders.removeItem(orderId!, itemId),
    onSuccess: (updated) => {
      queryClient.setQueryData(['order', updated.id], updated);
      void queryClient.invalidateQueries({ queryKey: ['tables'] });
      void queryClient.invalidateQueries({ queryKey: ['menu-items'] });
    },
    onError: (caught) => failed(caught, 'Could not remove that item.'),
  });

  const reorderItems = useMutation({
    mutationFn: ({ course, itemIds }: { course: number; itemIds: string[] }) =>
      api.orders.reorderItems(orderId!, course, itemIds),
    onSuccess: (updated) => queryClient.setQueryData(['order', updated.id], updated),
    onError: (caught) => failed(caught, 'Could not reorder those items.'),
  });

  const cancelItem = useMutation({
    mutationFn: ({ itemId, reason }: { itemId: string; reason: string }) =>
      api.orders.cancelItem(orderId!, itemId, reason),
    onSuccess: (updated) => {
      queryClient.setQueryData(['order', updated.id], updated);
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['menu-items'] });
      setCancelTarget(null);
      setCancelReason('');
      toast.success('Item voided and kept on the record.');
    },
    onError: (caught) => failed(caught, 'Could not void that item.'),
  });

  const send = useMutation({
    mutationFn: (itemIds: string[] | undefined) =>
      api.orders.send(orderId!, itemIds ? { itemIds } : {}),
    onSuccess: (result) => {
      queryClient.setQueryData(['order', result.order.id], result.order);
      invalidate();
      setSelectedIds(new Set());
      const tickets = result.ticketIds.length;
      toast.success(
        tickets === 0
          ? `${result.sentItemIds.length} item(s) marked sent — no ticket needed.`
          : `Sent at ${formatClock(result.sentAt)} · ${tickets} ticket${tickets > 1 ? 's' : ''} raised.`,
      );
    },
    onError: (caught) => failed(caught, 'Could not send those items.'),
  });

  /** Clear every unsent line at once, for the "they changed their mind" case. */
  const discardUnsent = useMutation({
    mutationFn: async (itemIds: string[]) => {
      let latest: Order | null = null;
      for (const itemId of itemIds) latest = await api.orders.removeItem(orderId!, itemId);
      return latest;
    },
    onSuccess: (updated) => {
      if (updated) queryClient.setQueryData(['order', updated.id], updated);
      invalidate();
      setResolvingUnsent(false);
      setPaying(true);
    },
    onError: (caught) => failed(caught, 'Could not remove those items.'),
  });

  const closeOrder = useMutation({
    mutationFn: () => api.orders.close(orderId!),
    onSuccess: () => {
      invalidate();
      toast.success('Order closed. Table is free.');
      navigate('/pos');
    },
    onError: (caught) => failed(caught, 'Could not close the order.'),
  });

  if (loadingOrder) return <Spinner label="Opening order…" />;
  if (loadError || orderError) {
    return (
      <div className="p-8">
        <ErrorState error={loadError ?? orderError} onRetry={() => navigate(0)} />
      </div>
    );
  }
  if (!order) {
    return (
      <div className="p-8">
        <EmptyState title="No open order" message="This table has no active order." />
      </div>
    );
  }

  const handlers: OrderPanelHandlers = {
    onToggleSelect: (itemId) =>
      setSelectedIds((current) => {
        const next = new Set(current);
        if (next.has(itemId)) next.delete(itemId);
        else next.add(itemId);
        return next;
      }),
    onChangeQuantity: (item, next) => {
      if (next < 1) {
        removeItem.mutate(item.id);
        return;
      }
      updateItem.mutate({ itemId: item.id, body: { quantity: next } });
    },
    onRemove: (item) => removeItem.mutate(item.id),
    onCancel: (item) => {
      setCancelTarget(item);
      setCancelReason('');
    },
    onEditNote: (item) => {
      setNoteTarget(item);
      setNoteDraft(item.notes ?? '');
    },
    onMoveCourse: (item, nextCourse) =>
      updateItem.mutate({ itemId: item.id, body: { course: nextCourse } }),
    onReorder: (course, itemIds) => reorderItems.mutate({ course, itemIds }),
    onMoveTable: (item) => {
      setMoveTarget(item);
      setMoving(true);
    },
    onDiscount: allows('orders:discount') ? (item) => setDiscountTarget(item) : undefined,
  };

  // Nothing reaches the order on a bare tap: the sheet asks how many first, so
  // a mis-tap costs a Cancel rather than a line the kitchen has to be told about.
  const pickItem = (item: MenuItem) => setConfiguring(item);

  // The party can only be re-seated while the order still owns its table.
  const canReseat = ['OPEN', 'IN_PROGRESS', 'WAITING_PAYMENT'].includes(order.status);

  // A discount is house money, so it is permission-gated and only offered while the bill can still change.
  const canDiscount =
    allows('orders:discount') &&
    ['OPEN', 'IN_PROGRESS', 'WAITING_PAYMENT'].includes(order.status) &&
    (order.totals.subtotalCents > 0 || order.totals.discountCents > 0);

  const selectedList = [...selectedIds];
  const sendable = selectedList.length > 0 ? selectedList : draftItems.map((item) => item.id);
  const draftKitchen = draftItems.filter((item) => item.destination === 'KITCHEN').length;
  const draftBar = draftItems.filter((item) => item.destination === 'BAR').length;

  return (
    <div className="flex h-full flex-col bg-canvas">
      <header className="no-print flex shrink-0 flex-wrap items-center gap-x-6 gap-y-3 border-b border-line bg-surface px-4 py-3">
        <Button variant="ghost" size="sm" icon={<ArrowLeft size={16} />} onClick={() => navigate('/pos')}>
          Floor
        </Button>

        {/* Table and guest count are one control: both are things the floor
            gets wrong early and corrects later, and they are corrected together. */}
        <button
          type="button"
          disabled={!canReseat}
          onClick={() => setReseating(true)}
          title={canReseat ? 'Change table or guest count' : undefined}
          className={clsx(
            'group -mx-2 flex items-baseline gap-3 rounded-lg px-2 py-1 text-left transition',
            canReseat ? 'hover:bg-hover' : 'cursor-default',
          )}
        >
          <span className="font-display text-3xl leading-none text-ink">
            Table {String(order.tableNumber).padStart(2, '0')}
          </span>
          <span className="text-sm text-ink-faint">#{order.orderNumber}</span>
          {canReseat ? (
            <Pencil
              size={13}
              className="self-center text-ink-faint opacity-0 transition group-hover:opacity-100"
            />
          ) : null}
        </button>

        <div className="flex items-center gap-5 text-sm text-ink-soft">
          <button
            type="button"
            disabled={!canReseat}
            onClick={() => setReseating(true)}
            className={clsx(
              '-mx-1.5 inline-flex items-center gap-1.5 rounded px-1.5 py-1 transition',
              canReseat ? 'hover:bg-hover hover:text-ink' : 'cursor-default',
            )}
          >
            <Users size={15} /> {order.guestCount} guests
          </button>
          <span className="inline-flex items-center gap-1.5">
            <Clock size={15} /> Opened {formatClock(order.openedAt)}
          </span>
          <span className="tnum text-ink-faint">{formatWaiting(order.openedAt, now)}</span>
        </div>

        <div className="ml-auto flex items-center gap-4">
          <div className="text-right">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-ink-faint">
              Running total
            </div>
            <div className="tnum font-display text-2xl leading-none text-accent-strong">
              {money(order.grandTotalCents)}
            </div>
          </div>
          <Button
            variant="primary"
            size="lg"
            icon={<CreditCard size={18} />}
            onClick={() => (draftItems.length > 0 ? setResolvingUnsent(true) : setPaying(true))}
            disabled={order.grandTotalCents <= 0 || order.status === 'CLOSED'}
          >
            Payment
          </Button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        <div className="flex min-w-0 flex-1 flex-col pb-14 lg:pb-0">
          {/* Course selector governs where newly tapped items land. */}
          <div className="flex items-center gap-2 border-b border-line bg-surface/40 px-3 py-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-ink-faint">
              Adding to
            </span>
            {COURSES.map((number) => (
              <button
                key={number}
                type="button"
                onClick={() => setCourse(number)}
                className={clsx(
                  'h-9 w-9 rounded-lg border text-sm font-semibold transition',
                  course === number
                    ? 'border-accent bg-accent/15 text-accent-strong'
                    : 'border-line text-ink-soft hover:border-line-strong hover:text-ink',
                )}
              >
                {number}
              </button>
            ))}
          </div>

          <MenuBrowser
            categories={categories}
            items={menuItems}
            recentIds={recent}
            onPick={pickItem}
          />
        </div>

        {/* Below lg the ticket is a bottom sheet: the floor is worked from a phone,
            where a 26rem column would leave the menu grid no room at all. */}
        {sheetOpen ? (
          <button
            type="button"
            aria-label="Close order ticket"
            onClick={() => setSheetOpen(false)}
            className="no-print fixed inset-0 z-30 bg-scrim/45 lg:hidden"
          />
        ) : null}

        <aside
          className={clsx(
            'z-40 flex flex-col border-line bg-surface',
            'fixed inset-x-0 bottom-0 top-0 border-t shadow-xl transition-transform duration-300',
            sheetOpen ? 'translate-y-0' : 'translate-y-[calc(100%-3.5rem)]',
            'lg:static lg:z-auto lg:w-[26rem] lg:shrink-0 lg:translate-y-0 lg:border-l lg:border-t-0 lg:shadow-none lg:transition-none',
          )}
        >
          {/* Collapsed handle: keeps the running total on screen while browsing. */}
          <button
            type="button"
            onClick={() => setSheetOpen((open) => !open)}
            aria-expanded={sheetOpen}
            className="flex h-14 shrink-0 items-center gap-3 border-b border-line px-4 text-left lg:hidden"
          >
            <ChevronUp
              size={18}
              className={clsx('text-ink-faint transition-transform', sheetOpen && 'rotate-180')}
            />
            <span className="text-sm text-ink">
              {order.items.length === 0
                ? 'No items yet'
                : `${order.items.length} ${order.items.length === 1 ? 'item' : 'items'}`}
            </span>
            {draftItems.length > 0 ? (
              <span className="rounded-full bg-accent/15 px-2 py-0.5 text-[11px] text-accent-strong">
                {draftItems.length} unsent
              </span>
            ) : null}
            <span className="tnum ml-auto font-display text-2xl leading-none text-accent-strong">
              {money(order.totals.grandTotalCents)}
            </span>
          </button>

          <div className="flex-1 overflow-y-auto p-4">
            {order.items.length === 0 ? (
              <EmptyState title="Nothing ordered yet" message="Pick items from the menu to begin." />
            ) : (
              <OrderPanel
                order={order}
                selectedIds={selectedIds}
                handlers={handlers}
                onSelectCourse={(_, itemIds) =>
                  setSelectedIds((current) => {
                    const all = itemIds.every((id) => current.has(id));
                    const next = new Set(current);
                    for (const id of itemIds) {
                      if (all) next.delete(id);
                      else next.add(id);
                    }
                    return next;
                  })
                }
              />
            )}
          </div>

          <div className="shrink-0 border-t border-line bg-raised/40 p-4">
            <dl className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <dt className="text-ink-faint">Subtotal</dt>
                <dd className="tnum text-ink">{money(order.totals.subtotalCents)}</dd>
              </div>
              {order.totals.itemDiscountCents > 0 ? (
                <div className="flex justify-between">
                  <dt className="text-ink-faint">Item discounts</dt>
                  <dd className="tnum text-positive">−{money(order.totals.itemDiscountCents)}</dd>
                </div>
              ) : null}
              {order.totals.billDiscountCents > 0 ? (
                <div className="flex justify-between">
                  <dt className="text-ink-faint">
                    Bill discount
                    {order.totals.discountType === 'PERCENT'
                      ? ` ${formatRate(order.totals.discountValue)}%`
                      : ''}
                  </dt>
                  <dd className="tnum text-positive">−{money(order.totals.billDiscountCents)}</dd>
                </div>
              ) : null}
              {order.totals.serviceChargeCents > 0 ? (
                <div className="flex justify-between">
                  <dt className="text-ink-faint">
                    Service {formatRate(order.totals.serviceChargeBp)}%
                  </dt>
                  <dd className="tnum text-ink">{money(order.totals.serviceChargeCents)}</dd>
                </div>
              ) : null}
              {order.totals.taxBreakdown.map((bucket) => (
                <div key={`${bucket.taxType}-${bucket.rateBp}`} className="flex justify-between">
                  <dt className="text-ink-faint">
                    {bucket.taxType === 'ALCOHOL' ? 'Alcohol' : 'Food'} tax {formatRate(bucket.rateBp)}%
                  </dt>
                  <dd className="tnum text-ink">{money(bucket.taxCents)}</dd>
                </div>
              ))}
            </dl>

            {canDiscount ? (
              <button
                type="button"
                onClick={() => setDiscounting(true)}
                className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-accent transition hover:text-accent-strong"
              >
                <Percent size={12} />
                {order.totals.discountCents > 0 ? 'Change discount' : 'Add discount'}
              </button>
            ) : null}

            <div className="my-3 h-px bg-line" />

            <div className="mb-4 flex items-baseline justify-between">
              <span className="text-sm text-ink-faint">Total</span>
              <span className="tnum font-display text-3xl leading-none text-accent-strong">
                {money(order.totals.grandTotalCents)}
              </span>
            </div>

            {draftItems.length > 0 ? (
              <div className="mb-2 flex items-center gap-3 text-[11px] text-ink-faint">
                {draftKitchen > 0 ? (
                  <span className="inline-flex items-center gap-1">
                    <ChefHat size={12} /> {draftKitchen} kitchen
                  </span>
                ) : null}
                {draftBar > 0 ? (
                  <span className="inline-flex items-center gap-1">
                    <Wine size={12} /> {draftBar} bar
                  </span>
                ) : null}
              </div>
            ) : null}

            {order.items.some((item) => item.status !== 'CANCELLED') ? (
              <Button
                variant="secondary"
                block
                className="mb-2"
                icon={<ArrowRightLeft size={16} />}
                onClick={() => {
                  setMoveTarget(null);
                  setMoving(true);
                }}
              >
                Move items to another table
              </Button>
            ) : null}

            <Button
              variant="primary"
              size="lg"
              block
              icon={<Send size={18} />}
              loading={send.isPending}
              disabled={sendable.length === 0}
              onClick={() => send.mutate(selectedList.length > 0 ? selectedList : undefined)}
            >
              {selectedList.length > 0
                ? `Send ${selectedList.length} selected`
                : draftItems.length > 0
                  ? `Send all ${draftItems.length} items`
                  : 'Nothing to send'}
            </Button>
          </div>
        </aside>
      </div>

      <ModifierDialog
        item={configuring}
        open={configuring !== null}
        defaultCourse={course}
        busy={addItem.isPending}
        onCancel={() => {
          if (!addItem.isPending) setConfiguring(null);
        }}
        onConfirm={(draft) => {
          if (!configuring || addItem.isPending) return;
          // The sheet closes on the server's answer, not on the tap: a failed
          // add leaves the draft on screen to retry instead of losing it.
          addItem.mutate(
            { item: configuring, draft },
            { onSuccess: () => setConfiguring(null) },
          );
        }}
      />

      {/* The whole screen is held while the add is in flight — a second tap
          during the round trip would put the dish on the ticket twice. */}
      <BusyOverlay open={addItem.isPending} label="Adding to order…" />

      <SeatingDialog
        order={order}
        tables={tables ?? []}
        open={reseating}
        busy={reseat.isPending}
        onCancel={() => {
          if (!reseat.isPending) setReseating(false);
        }}
        onConfirm={(change) => reseat.mutate(change)}
      />

      <BusyOverlay open={reseat.isPending} label="Moving the party…" />

      <Modal
        open={noteTarget !== null}
        onClose={() => setNoteTarget(null)}
        title="Item note"
        subtitle={noteTarget?.nameSnapshot}
        footer={
          <>
            <Button variant="ghost" onClick={() => setNoteTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                if (noteTarget) {
                  updateItem.mutate({
                    itemId: noteTarget.id,
                    body: { notes: noteDraft.trim() },
                  });
                }
                setNoteTarget(null);
              }}
            >
              Save note
            </Button>
          </>
        }
      >
        <Field label="Note for the pass">
          <Textarea
            rows={3}
            autoFocus
            value={noteDraft}
            onChange={(event) => setNoteDraft(event.target.value)}
            placeholder="No onion · Less spicy · Allergy: peanuts"
          />
        </Field>
      </Modal>

      <ConfirmDialog
        open={cancelTarget !== null}
        title="Void this item?"
        message="It has already gone to the kitchen or bar. The item stays on the record with the reason you give."
        confirmLabel="Void item"
        loading={cancelItem.isPending}
        onCancel={() => setCancelTarget(null)}
        onConfirm={() =>
          cancelTarget &&
          cancelReason.trim() &&
          cancelItem.mutate({ itemId: cancelTarget.id, reason: cancelReason.trim() })
        }
      >
        <Field label="Reason" hint="Required — it appears in the audit log.">
          <Textarea
            rows={2}
            autoFocus
            value={cancelReason}
            onChange={(event) => setCancelReason(event.target.value)}
            placeholder="Guest changed their mind"
          />
        </Field>
      </ConfirmDialog>

      {/* The bill cannot include items the kitchen never saw, so unsent lines
          must be resolved one way or the other before the till opens. */}
      <Modal
        open={resolvingUnsent}
        onClose={() => setResolvingUnsent(false)}
        title="Some items have not been sent"
        subtitle={`${draftItems.length} line${draftItems.length === 1 ? '' : 's'} never reached the kitchen or bar.`}
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setResolvingUnsent(false)}>
              Back to order
            </Button>
            <Button
              variant="danger"
              loading={discardUnsent.isPending}
              onClick={() => discardUnsent.mutate(draftItems.map((item) => item.id))}
            >
              Remove them
            </Button>
            <Button
              variant="primary"
              loading={send.isPending}
              onClick={() =>
                send.mutate(undefined, {
                  onSuccess: () => {
                    setResolvingUnsent(false);
                    setPaying(true);
                  },
                })
              }
            >
              Send now
            </Button>
          </>
        }
      >
        <ul className="space-y-1.5 text-sm text-ink-soft">
          {draftItems.map((item) => (
            <li key={item.id} className="flex justify-between gap-3">
              <span>
                <span className="tnum text-accent">{item.quantity}×</span> {item.nameSnapshot}
              </span>
              <span className="text-ink-faint">Course {item.course}</span>
            </li>
          ))}
        </ul>
        <p className="mt-4 text-xs text-ink-faint">
          Send them so they are prepared and billed, or remove them from the order.
        </p>
      </Modal>

      <MoveItemDialog
        order={order}
        preselect={moveTarget}
        open={moving}
        onClose={() => {
          setMoving(false);
          setMoveTarget(null);
        }}
        onMoved={() => invalidate()}
      />

      <ItemDiscountDialog
        order={order}
        item={discountTarget}
        open={discountTarget !== null}
        onClose={() => setDiscountTarget(null)}
      />

      <DiscountDialog order={order} open={discounting} onClose={() => setDiscounting(false)} />

      <PaymentDialog
        order={order}
        open={paying}
        onClose={() => setPaying(false)}
        onSettled={(settled) => {
          setPaying(false);
          setReceiptOrder(settled);
        }}
      />

      <Modal
        open={receiptOrder !== null}
        onClose={() => setReceiptOrder(null)}
        title={receiptOrder?.isInvite ? 'Closed as an invitation' : 'Payment complete'}
        subtitle={
          receiptOrder?.isInvite
            ? 'Nothing was charged. Print the bill for the file if you want a copy.'
            : 'Print the receipt, then close the table.'
        }
        size="md"
        footer={
          <>
            <PrintReceiptButton />
            {receiptOrder ? (
              <SendToPrinterButton orderId={receiptOrder.id} kind="RECEIPT" variant="secondary" />
            ) : null}
            <Button
              variant="primary"
              size="lg"
              loading={closeOrder.isPending}
              onClick={() =>
                // With invoicing on, a paid table must first say who the invoice is for.
                settings?.invoicingEnabled && receiptOrder && !receiptOrder.isInvite
                  ? setChoosingBuyer(true)
                  : closeOrder.mutate()
              }
            >
              Close table
            </Button>
          </>
        }
      >
        {receiptOrder && settings?.invoicingEnabled ? (
          <div className="mb-3">
            <InvoiceBuyerSummary order={receiptOrder} />
          </div>
        ) : null}
        {receiptOrder ? <ReceiptPreview orderId={receiptOrder.id} /> : null}
      </Modal>

      {receiptOrder ? (
        <CloseOrderDialog
          order={receiptOrder}
          open={choosingBuyer}
          closing={closeOrder.isPending}
          onClose={() => setChoosingBuyer(false)}
          onConfirm={() => closeOrder.mutate()}
        />
      ) : null}
    </div>
  );
}

/** Fetches the finished receipt for the just-paid order. */
function ReceiptPreview({ orderId }: { orderId: string }) {
  const [receipt, setReceipt] = useState<Awaited<ReturnType<typeof api.orders.receipt>> | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let cancelled = false;
    api.orders
      .receipt(orderId)
      .then((result) => !cancelled && setReceipt(result))
      .catch((caught: unknown) => !cancelled && setError(caught));
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  if (error) return <ErrorState error={error} />;
  if (!receipt) return <Spinner />;
  return <ReceiptSheet receipt={receipt} />;
}
